import { useEffect } from 'react';
import { SITE_URL } from '../config/journalDefaults';
import { useJournalSettings } from '../hooks/useJournalSettings';
import { setCanonical, setRobots } from '../hooks/useDocumentMeta';

// Sign-in pages, dashboards and the editorial back office: nothing a search engine should
// list. Whole path segments only, so '/editor' covers '/editor/login' but not the public
// '/editorial-board'.
const PRIVATE_SECTIONS = [
  '/login', '/dashboard', '/desk', '/admin', '/author', '/reviewer', '/design-system',
  '/editor', '/managing-editor', '/associate-editor', '/editor-in-chief',
  '/ethics-quality-editor', '/editorial-office', '/editorial-assistant', '/section-editor',
];

// Titles for public-facing sign-in pages, which have no static page of their own and
// would otherwise keep the site's default title.
const PAGE_TITLES: [RegExp, string][] = [
  [/^\/login$/, 'Editorial Team Sign In'],
  [/^\/author\/login$/, 'Author Sign In'],
  [/^\/author\/register$/, 'Author Registration'],
  [/^\/author\/reset-password$/, 'Reset Your Password'],
  [/^\/author\/onboarding$/, 'Complete Your Author Profile'],
  [/^\/reviewer\/login$/, 'Reviewer Sign In'],
  [/^\/reviewer\/(forgot|reset)-password$/, 'Reset Your Password'],
  [/^\/reviewer\/invitation\//, 'Review Invitation'],
  [/^\/reviewer\/first-login\//, 'Set Up Your Reviewer Account'],
];

export const isPrivatePath = (pathname: string) =>
  PRIVATE_SECTIONS.some((section) => pathname === section || pathname.startsWith(`${section}/`));

/**
 * Keeps the per-address head tags right on every navigation: one canonical tag naming
 * this address (updated in place, so a prerendered page never ends up with two),
 * noindex on private pages, and a real title on the sign-in pages. A page can still
 * override any of these in its own effect, which runs after this one.
 */
export function useRouteHead(pathname: string) {
  const settings = useJournalSettings();
  const journal = settings.journalAbbreviation || settings.journalName;

  useEffect(() => {
    const path = pathname.replace(/\/+$/, '') || '/';
    setCanonical(`${SITE_URL}${path === '/' ? '/' : path}`);
    setRobots(isPrivatePath(path) ? 'noindex, nofollow' : 'index, follow');
    const title = PAGE_TITLES.find(([route]) => route.test(path))?.[1];
    if (title && !settings.settingsLoading) document.title = `${title} | ${journal}`;
  }, [pathname, journal, settings.settingsLoading]);
}
